import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import api from '../../lib/api';
import { toast } from 'sonner';
import { 
  Users as UsersIcon, 
  Plus, 
  Search, 
  Edit, 
  Eye, 
  Power, 
  Key, 
  Shield,
  MoreVertical,
  UserPlus,
  Filter,
  X,
  Clock,
  MapPin,
  Mail,
  Phone,
  Building2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ role: '', unit: '', status: '' });
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditSheet, setShowEditSheet] = useState(false);
  const [showViewSheet, setShowViewSheet] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'staff',
    assigned_unit: '',
    password: '',
    companies: []
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, locationsRes, companiesRes] = await Promise.all([
        api.get('/admin/users', { params: filters }),
        api.get('/admin/roles'),
        api.get('/locations'),
        api.get('/admin/companies')
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setLocations(locationsRes.data);
      setAllCompanies(companiesRes.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load users');
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      if (!formData.name || !formData.email || !formData.phone || !formData.role) {
        toast.error('Please fill all required fields');
        return;
      }
      if (formData.role !== 'admin' && formData.companies.length === 0) {
        toast.error('Please assign at least one company');
        return;
      }
      const payload = {
        ...formData,
        companies: formData.companies.map(cid => ({ company_id: cid, role: formData.role }))
      };
      const response = await api.post('/admin/users', payload);
      toast.success(`User created! Default password: ${response.data.default_password}`);
      setShowCreateDialog(false);
      setFormData({ name: '', email: '', phone: '', role: 'staff', assigned_unit: '', password: '', companies: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        role: formData.role,
        assigned_unit: formData.assigned_unit || null,
        companies: formData.companies.map(cid => ({ company_id: cid, role: formData.role }))
      };
      await api.put(`/admin/users/${selectedUser.user_id}`, payload);
      toast.success('User updated successfully');
      setShowEditSheet(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await api.put(`/users/${user.user_id}/toggle-active`);
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await api.post(`/admin/users/${selectedUser.user_id}/reset-password`);
      setNewPassword(response.data.new_password);
      toast.success('Password reset successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const openEditSheet = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      assigned_unit: user.assigned_unit || '',
      password: '',
      companies: (user.companies || []).map(c => c.company_id)
    });
    setShowEditSheet(true);
  };

  const openViewSheet = (user) => {
    setSelectedUser(user);
    setShowViewSheet(true);
  };

  const openResetDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetDialog(true);
  };

  const resetFilters = () => {
    setFilters({ role: '', unit: '', status: '' });
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.phone?.includes(searchTerm)
  );

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'staff': return 'bg-slate-100 text-slate-800 border-slate-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const toggleCompany = (companyId) => {
    setFormData(prev => ({
      ...prev,
      companies: prev.companies.includes(companyId)
        ? prev.companies.filter(id => id !== companyId)
        : [...prev.companies, companyId]
    }));
  };

  const CompanyCheckboxes = () => (
    <div data-testid="company-assignment-section" className="space-y-2">
      <Label>Company Access {formData.role !== 'admin' && '*'}</Label>
      {formData.role === 'admin' ? (
        <p className="text-xs text-slate-500">Admins automatically have access to all companies.</p>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-2">Select which companies this user can access</p>
          <div className="space-y-2 border rounded-lg p-3 bg-slate-50/50">
            {allCompanies.map(company => (
              <label key={company.company_id} className="flex items-center gap-3 cursor-pointer py-1.5 hover:bg-white px-2 rounded transition-colors">
                <Checkbox
                  data-testid={`company-checkbox-${company.code}`}
                  checked={formData.companies.includes(company.company_id)}
                  onCheckedChange={() => toggleCompany(company.company_id)}
                />
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 theme-text" />
                  <span className="text-sm font-medium">{company.name}</span>
                  <span className="text-xs text-slate-400">{company.code}</span>
                </div>
              </label>
            ))}
            {allCompanies.length === 0 && (
              <p className="text-xs text-slate-400">No companies available</p>
            )}
          </div>
        </>
      )}
    </div>
  );

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              User Management
            </h1>
            <p className="text-slate-600 mt-1">Manage system users and their access</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-btn" className="bg-slate-900 hover:bg-slate-800">
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>Add a new user and assign company access</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label>Mobile Number *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="9876543210"
                  />
                </div>
                <div>
                  <Label>Role *</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Assigned Unit</Label>
                  <Select value={formData.assigned_unit} onValueChange={(v) => setFormData({...formData, assigned_unit: v === 'none' ? '' : v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific unit</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.location_id} value={loc.location_id}>{loc.location_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Password (optional)</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Leave empty for default: Welcome@123"
                  />
                </div>
                <CompanyCheckboxes />
                <Button data-testid="create-user-submit-btn" onClick={handleCreateUser} className="w-full bg-slate-900 hover:bg-slate-800">
                  Create User
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <Badge className="ml-2 bg-slate-900 text-white">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
              <div>
                <Label className="text-xs">Role</Label>
                <Select value={filters.role} onValueChange={(v) => setFilters({...filters, role: v === 'all' ? '' : v})}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Unit</Label>
                <Select value={filters.unit} onValueChange={(v) => setFilters({...filters, unit: v === 'all' ? '' : v})}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All units" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All units</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>{loc.location_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v === 'all' ? '' : v})}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset Filters
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            Users ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredUsers.map((user) => (
              <div 
                key={user.user_id} 
                data-testid={`user-row-${user.user_id}`}
                className={`p-4 border rounded-lg hover:bg-slate-50 transition-colors ${!user.is_active ? 'opacity-60 bg-slate-50' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-slate-600 font-semibold">{user.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{user.name}</span>
                          <Badge className={getRoleBadgeColor(user.role)}>
                            {user.role}
                          </Badge>
                          {!user.is_active && (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-slate-600 mt-3 pl-13">
                      <div className="flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" />
                        {user.phone}
                      </div>
                      {user.assigned_unit_name && (
                        <div className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" />
                          {user.assigned_unit_name}
                        </div>
                      )}
                      {user.companies && user.companies.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap md:col-span-2">
                          <Building2 className="h-3.5 w-3.5 flex-shrink-0 theme-text" />
                          {user.companies.map(c => (
                            <Badge key={c.company_id} variant="outline" className="text-[10px] py-0 theme-company-badge">
                              {c.company_id}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {user.last_login && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          Last: {new Date(user.last_login).toLocaleDateString()}
                        </div>
                      )}
                      {user.created_by_name && (
                        <div className="text-xs text-slate-400">
                          Created by: {user.created_by_name}
                        </div>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openViewSheet(user)}>
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditSheet(user)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit User
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                        <Power className="h-4 w-4 mr-2" />
                        {user.is_active ? 'Disable' : 'Enable'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openResetDialog(user)}>
                        <Key className="h-4 w-4 mr-2" />
                        Reset Password
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <UsersIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No users found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Sheet */}
      <Sheet open={showEditSheet} onOpenChange={setShowEditSheet}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Edit User</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 pt-6">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div>
              <Label>Mobile Number *</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>
            <div>
              <Label>Role *</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigned Unit</Label>
              <Select value={formData.assigned_unit} onValueChange={(v) => setFormData({...formData, assigned_unit: v === 'none' ? '' : v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific unit</SelectItem>
                  {locations.map(loc => (
                    <SelectItem key={loc.location_id} value={loc.location_id}>{loc.location_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <CompanyCheckboxes />
            <Button data-testid="save-user-btn" onClick={handleUpdateUser} className="w-full bg-slate-900 hover:bg-slate-800">
              Save Changes
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* View Sheet */}
      <Sheet open={showViewSheet} onOpenChange={setShowViewSheet}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>User Details</SheetTitle>
          </SheetHeader>
          {selectedUser && (
            <div className="space-y-6 pt-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center">
                  <span className="text-2xl text-slate-600 font-semibold">{selectedUser.name?.charAt(0)}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{selectedUser.name}</h3>
                  <Badge className={getRoleBadgeColor(selectedUser.role)}>{selectedUser.role}</Badge>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Mail className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium">{selectedUser.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Mobile</p>
                    <p className="font-medium">{selectedUser.phone}</p>
                  </div>
                </div>
                {selectedUser.assigned_unit_name && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <MapPin className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Assigned Unit</p>
                      <p className="font-medium">{selectedUser.assigned_unit_name}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Clock className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Last Login</p>
                    <p className="font-medium">{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Shield className="h-5 w-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Status</p>
                    <Badge className={selectedUser.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {selectedUser.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-5 w-5 text-slate-400" />
                    <p className="text-xs text-slate-500">Company Access</p>
                  </div>
                  {selectedUser.role === 'admin' ? (
                    <p className="text-sm text-slate-600">All companies (Admin)</p>
                  ) : selectedUser.companies && selectedUser.companies.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedUser.companies.map(c => (
                        <Badge key={c.company_id} variant="outline" className="theme-company-badge">
                          {c.company_id}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-red-500">No companies assigned</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              {newPassword 
                ? `Password has been reset for ${selectedUser?.name}`
                : `Are you sure you want to reset the password for ${selectedUser?.name}?`
              }
            </DialogDescription>
          </DialogHeader>
          {newPassword ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 mb-2">New Password:</p>
                <p className="text-lg font-mono font-bold text-green-900">{newPassword}</p>
              </div>
              <p className="text-sm text-slate-500">Please share this password with the user securely.</p>
              <Button onClick={() => setShowResetDialog(false)} className="w-full">
                Done
              </Button>
            </div>
          ) : (
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancel</Button>
              <Button onClick={handleResetPassword} className="bg-amber-600 hover:bg-amber-700">
                Reset Password
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
